import Link from 'next/link';

import { useRouter } from 'next/router';
import { FC, useContext } from 'react';

import { useTranslation } from 'utils/i18n';
import { AuthContext } from '../../state/auth';

import ModalDialog from '../modal';

export type ModalType =
  | 'sign-in'
  | 'space-type-clinical-error'
  | 'space-type-non-clinical-error'
  | 'service-clash'
  | 'add-to-booking-failure'
  | 'space-count-warning'
  | 'unapproved-user-warning'
  | 'no-service-error'
  | 'no-space-error';

interface Props {
  openModal: ModalType | null;
  onClose: () => void;
}

const SpaceListModals: FC<Props> = ({ openModal, onClose: close }) => {
  const router = useRouter();
  const { actions: { setReturnUrl } = {} } = useContext(AuthContext);
  const { t } = useTranslation('common');

  return (
    <>
      <ModalDialog
        isOpen={openModal === 'sign-in'}
        onClose={close}
        showCloseIcon
        headerLabel="Your Booking"
        size="small"
        primaryAction={
          <Link
            href={t('navigation.register.url')}
            className="button button--primary"
          >
            {t('space_list.modal.register')}
          </Link>
        }
        secondaryAction={
          <Link
            href={t('navigation.sign_in.url')}
            className="button button--secondary"
            onClick={() =>
              (setReturnUrl as (url: string) => void)(router.asPath)
            }
          >
            {t('space_list.modal.sign_in')}
          </Link>
        }
      >
        <p>{t('space_list.modal.paragraph_one')}</p>
        <p>{t('space_list.modal.paragraph_two')}</p>
      </ModalDialog>
      <ModalDialog
        isOpen={openModal === 'space-type-clinical-error'}
        onClose={close}
        showCloseIcon
        headerLabel={t('search_results.service_clash.header')}
        size="xsmall"
        primaryAction={
          <button
            type="button"
            className="button button--secondary"
            onClick={close}
          >
            {t('search_results.modal.close')}
          </button>
        }
      >
        <p>{t('search_results.errors.space_type.clinical')}</p>
      </ModalDialog>
      <ModalDialog
        isOpen={openModal === 'space-type-non-clinical-error'}
        onClose={close}
        showCloseIcon
        headerLabel={t('search_results.service_clash.header')}
        size="xsmall"
        primaryAction={
          <button
            type="button"
            className="button button--secondary"
            onClick={close}
          >
            {t('search_results.modal.close')}
          </button>
        }
      >
        <p>{t('search_results.errors.space_type.non_clinical')}</p>
      </ModalDialog>
      <ModalDialog
        isOpen={openModal === 'service-clash'}
        onClose={close}
        showCloseIcon
        headerLabel={t('search_results.service_clash.header')}
        size="xsmall"
        primaryAction={
          <button
            type="button"
            className="button button--secondary"
            onClick={close}
          >
            {t('search_results.modal.close')}
          </button>
        }
      >
        <p>{t('search_results.service_clash.message')}</p>
      </ModalDialog>
      <ModalDialog
        isOpen={openModal === 'add-to-booking-failure'}
        onClose={close}
        showCloseIcon={false}
        headerLabel={t('search_results.modal.header')}
        size="xsmall"
        primaryAction={
          <button
            type="button"
            className="button button--secondary"
            onClick={close}
          >
            {t('search_results.modal.close')}
          </button>
        }
      >
        <p className="message">{t('search_results.modal.message')}</p>
      </ModalDialog>
      <ModalDialog
        isOpen={openModal === 'space-count-warning'}
        onClose={close}
        headerLabel={t('quick_book.maximum_spaces.title')}
        size="xsmall"
        primaryAction={
          <button className="button button--primary" onClick={close}>
            {t('shared.ok')}
          </button>
        }
      >
        <p data-testid="maximumSpacesReached">
          {t('quick_book.maximum_spaces.message')}
        </p>
      </ModalDialog>
      <ModalDialog
        isOpen={openModal === 'unapproved-user-warning'}
        onClose={close}
        headerLabel={t('quick_book.unapproved_user.title')}
        size="xsmall"
        primaryAction={
          <button className="button button--primary" onClick={close}>
            {t('shared.ok')}
          </button>
        }
      >
        <p data-testid="unapprovedUser">
          {t('quick_book.unapproved_user.message')}
        </p>
      </ModalDialog>
      <ModalDialog
        isOpen={openModal === 'no-service-error'}
        onClose={close}
        showCloseIcon={false}
        headerLabel={t('search_results.modal.header')}
        size="xsmall"
        primaryAction={
          <button
            type="button"
            className="button button--secondary"
            onClick={close}
          >
            {t('search_results.modal.close')}
          </button>
        }
      >
        <p className="message">{t('venue_calendar.no_service.message')}</p>
      </ModalDialog>
      <ModalDialog
        isOpen={openModal === 'no-space-error'}
        onClose={close}
        showCloseIcon={false}
        headerLabel={t('search_results.modal.header')}
        size="xsmall"
        primaryAction={
          <button
            type="button"
            className="button button--secondary"
            onClick={close}
          >
            {t('search_results.modal.close')}
          </button>
        }
      >
        <p className="message">{t('venue_calendar.no_space.message')}</p>
      </ModalDialog>
    </>
  );
};

export default SpaceListModals;
